import { z } from "zod"

export * from "./collision"
export * from "./createNormalFn"
export * from "./findOffsetAngle"
export * from "./findCenterOfBox"
export * from "./findClosestPointOnLine"
export * from "./findDistanceBetweenPoints"
export * from "./findDistanceBetweenPointAndLine"
export * from "./findMinMaxPoints"
export * from "./inset"
export * from "./isomorphic"
export * from "./moveOnVector"
export * from "./nameShape"
export * from "./offsetPointPairs"
export * from "./rotatePoint"
export * from "./snapping"
export * from "./sortNodes"
export * from "./transmute"
export * from "./rotatePanel"
export * from "./findBearingBetweenTwoPoints"
export * from "./linearFnFromPointSlope"
export * from "./findCenterOfPoints"
export * from "./findMinMaxPoints"

export const scale2dSchema = z.object({
  w: z.number(),
  h: z.number(),
})

export type Scale2d = z.infer<typeof scale2dSchema>

export function isScale2d(x: unknown): x is Scale2d {
  return (
    typeof x === `object` &&
    typeof (x as Scale2d).w === `number` &&
    typeof (x as Scale2d).h === `number`
  )
}

export const point2DSchema = z.object({
  x: z.number(),
  y: z.number(),
})
export type Point2d = z.infer<typeof point2DSchema>

export function isPoint2d(x: unknown): x is Point2d {
  return (
    typeof x === `object` &&
    x !== null &&
    typeof (x as Point2d).x === `number` &&
    typeof (x as Point2d).y === `number`
  )
}

export type Segment2d = {
  p1: Point2d
  p2: Point2d
}

export const isSegment2d = (input: unknown): input is Segment2d =>
  typeof input === `object` &&
  input !== null &&
  `p1` in input &&
  `p2` in input &&
  isPoint2d(input.p1) &&
  isPoint2d(input.p2)

export type Polygon2d = {
  nodeIds: string[]
}
export function isPolygon2d(x: unknown): x is Polygon2d {
  return (
    typeof x === `object` &&
    Array.isArray((x as Polygon2d).nodeIds) &&
    (x as Polygon2d).nodeIds.every((y) => typeof y === `string`)
  )
}

export type Vector2d = { d: number; r: number }

export type Bounds2d = {
  x: {
    right: number
    left: number
  }
  y: {
    top: number
    bottom: number
  }
}

export type Ray2d = {
  (x: number): number | undefined
  origin: Point2d
  slope: number
}

export type LinearFn2d = {
  (x: number): number
  slope: number
  intercept: number
}

export const rectangle2dSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  r: z.number(),
})
export type Rectangle2d = z.infer<typeof rectangle2dSchema>

export function isRectangle2d(x: unknown): x is Rectangle2d {
  return isPoint2d(x) && isScale2d(x) && typeof (x as Rectangle2d).r === `number`
}

export type Coordinate2d = {
  lat: number
  lng: number
}
